/**
 * financeReportModel — pure aggregation for the "דוח כספים לייצוא" report.
 *
 * Input: raw transactions (+ appointments for the treatment-type / therapist
 * join) and the user's options. Output: everything both the on-screen preview
 * (recharts) and the PDF callable (server-side SVG) need, already formatted.
 *
 * Money semantics mirror FinanceReportSection / monthly analytics:
 *   collected = paid + invoice_manual + invoice_auto
 *   pending   = pending (+ legacy pending_collection)
 *   expected  = expected (future)
 *   refunded  = refunded
 *   split / cancelled never count as money (split = internal parent record)
 * Effective date of a row = actualPaymentDate || expectedPaymentDate || createdAt.
 */

import {
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHOD_COLORS,
} from "../../../../../constants/paymentMethods";

export const FIN_STATUS_OPTIONS = [
  { key: "paid", label: "שולם", color: "#22c55e" },
  { key: "invoiced", label: "חשבונית", color: "#8b5cf6" },
  { key: "pending", label: "ממתין לתשלום", color: "#f59e0b" },
  { key: "expected", label: "צפוי (עתידי)", color: "#3b82f6" },
  { key: "refunded", label: "זיכוי", color: "#ef4444" },
  { key: "cancelled", label: "בוטל", color: "#9ca3af" },
];

export const FIN_DEFAULT_STATUSES = ["paid", "invoiced", "pending", "expected", "refunded"];

export const FIN_CHART_OPTIONS = [
  { key: "statusPie", label: "התפלגות סטטוסים", type: "pie", defaultOn: true },
  { key: "methodPie", label: "חלוקה לפי אמצעי תשלום", type: "pie", defaultOn: true },
  { key: "treatmentBars", label: "הכנסות לפי סוג טיפול", type: "bar", defaultOn: true },
  {
    key: "monthBars",
    label: "נגבה מול צפוי לפי חודש",
    type: "bar",
    defaultOn: true,
    multiMonthOnly: true,
  },
];

export const FIN_SECTION_OPTIONS = [
  { key: "kpis", label: "מדדים מרכזיים (KPI)", defaultOn: true },
  { key: "transactions", label: "פירוט תנועות", defaultOn: true },
  { key: "byPatient", label: "סיכום לפי מטופל", defaultOn: true },
];

const COLLECTED = new Set(["paid", "invoice_manual", "invoice_auto"]);

/** Raw Firestore status → report bucket key (FIN_STATUS_OPTIONS.key). */
export const bucketOf = (status) => {
  if (status === "paid") return "paid";
  if (status === "invoice_manual" || status === "invoice_auto") return "invoiced";
  if (status === "pending" || status === "pending_collection") return "pending";
  if (status === "expected") return "expected";
  if (status === "refunded") return "refunded";
  if (status === "cancelled") return "cancelled";
  return null; // split & unknown → excluded
};

const bucketLabel = (key) => FIN_STATUS_OPTIONS.find((s) => s.key === key)?.label || key;
const bucketColor = (key) => FIN_STATUS_OPTIONS.find((s) => s.key === key)?.color || "#9ca3af";

export const toDate = (v) => {
  if (!v) return null;
  const d = v.seconds ? new Date(v.seconds * 1000) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const effectiveDate = (tx) =>
  toDate(tx.actualPaymentDate) || toDate(tx.expectedPaymentDate) || toDate(tx.createdAt);

export const formatCurrency = (amount) =>
  `₪${Math.round(Number(amount) || 0).toLocaleString("he-IL")}`;

const formatDate = (d) =>
  d
    ? d.toLocaleDateString("he-IL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Asia/Jerusalem",
      })
    : "—";

const monthKeyOf = (d) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  return `${y}-${m}`;
};

const monthLabelOf = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1, 12)).toLocaleDateString("he-IL", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });
};

const TREATMENT_COLORS = [
  "#4dd9b4", "#3b82f6", "#8b5cf6", "#f59e0b", "#ec4899",
  "#22c55e", "#06b6d4", "#f97316", "#84cc16", "#9ca3af",
];

/** Number of distinct Israel months between two yyyy-mm-dd strings (inclusive). */
export const monthsInRange = (startIso, endIso) => {
  if (!startIso || !endIso) return 0;
  const [sy, sm] = startIso.split("-").map(Number);
  const [ey, em] = endIso.split("-").map(Number);
  return (ey - sy) * 12 + (em - sm) + 1;
};

/**
 * @param {Object} params
 * @param {Array} params.transactions   serialized financial_transactions
 * @param {Array} params.appointments   serialized appointments (same range) for joins
 * @param {string} params.startDate     yyyy-mm-dd
 * @param {string} params.endDate       yyyy-mm-dd
 * @param {Set<string>} params.statuses enabled bucket keys
 * @param {Object} params.charts        { [chartKey]: boolean }
 * @param {Object} params.sections      { [sectionKey]: boolean }
 */
export function buildFinanceReport({
  transactions = [],
  appointments = [],
  startDate,
  endDate,
  statuses,
  charts = {},
  sections = {},
}) {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59.999`);
  const aptById = new Map();
  appointments.forEach((a) => aptById.set(a.id, a));

  // ─── Normalize + filter rows ───
  const rows = [];
  transactions.forEach((tx) => {
    const bucket = bucketOf(tx.status);
    if (!bucket) return;
    const date = effectiveDate(tx);
    if (!date || date < start || date > end) return;
    if (!statuses.has(bucket)) return;
    const apt = tx.appointmentId ? aptById.get(tx.appointmentId) : null;
    rows.push({
      id: tx.id,
      date,
      bucket,
      amount: Number(tx.amount) || 0,
      patientId: tx.patientId || null,
      patientName: tx.patientName || tx.description || "—",
      therapistName: apt?.therapistName || "—",
      treatmentTypeName: apt?.treatmentTypeName || (tx.isPunchCard ? "כרטיסיה" : "לא צוין"),
      paymentMethod: tx.paymentMethod || null,
      isCollected: COLLECTED.has(tx.status),
    });
  });
  rows.sort((a, b) => a.date - b.date);

  // ─── Totals ───
  const totals = { collected: 0, collectedCount: 0, pending: 0, expected: 0, refunded: 0 };
  rows.forEach((r) => {
    if (r.isCollected) {
      totals.collected += r.amount;
      totals.collectedCount += 1;
    } else if (r.bucket === "pending") totals.pending += r.amount;
    else if (r.bucket === "expected") totals.expected += r.amount;
    else if (r.bucket === "refunded") totals.refunded += r.amount;
  });
  const billable = totals.collected + totals.pending + totals.expected;
  const collectionRate = billable > 0 ? Math.round((totals.collected / billable) * 100) : 0;

  const kpis = [
    { key: "collected", label: "נגבה בפועל", value: formatCurrency(totals.collected) },
    { key: "count", label: "מספר תשלומים", value: String(totals.collectedCount) },
    {
      key: "avg",
      label: "ממוצע לתשלום",
      value: formatCurrency(
        totals.collectedCount > 0 ? totals.collected / totals.collectedCount : 0
      ),
    },
    { key: "rate", label: "אחוז גבייה", value: `${collectionRate}%` },
    { key: "pending", label: "ממתין לתשלום", value: formatCurrency(totals.pending) },
    { key: "expected", label: "צפוי (עתידי)", value: formatCurrency(totals.expected) },
    { key: "refunded", label: "זיכויים", value: formatCurrency(totals.refunded) },
  ];

  // ─── Charts ───
  const chartList = [];

  if (charts.statusPie) {
    const byBucket = new Map();
    rows.forEach((r) => byBucket.set(r.bucket, (byBucket.get(r.bucket) || 0) + r.amount));
    const data = FIN_STATUS_OPTIONS.filter((s) => byBucket.get(s.key) > 0).map((s) => ({
      label: s.label,
      value: Math.round(byBucket.get(s.key)),
      color: s.color,
    }));
    chartList.push({ key: "statusPie", type: "pie", title: "התפלגות סטטוסים (סכומים)", data });
  }

  if (charts.methodPie) {
    const byMethod = new Map();
    rows
      .filter((r) => r.isCollected)
      .forEach((r) => {
        const k = r.paymentMethod || "unknown";
        byMethod.set(k, (byMethod.get(k) || 0) + r.amount);
      });
    const data = [...byMethod.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => ({
        label: k === "unknown" ? "לא צוין" : PAYMENT_METHOD_LABELS[k] || k,
        value: Math.round(v),
        color: PAYMENT_METHOD_COLORS[k] || "#9ca3af",
      }));
    chartList.push({
      key: "methodPie",
      type: "pie",
      title: "חלוקה לפי אמצעי תשלום (נגבה)",
      data,
    });
  }

  if (charts.treatmentBars) {
    const byType = new Map();
    rows
      .filter((r) => r.isCollected)
      .forEach((r) => byType.set(r.treatmentTypeName, (byType.get(r.treatmentTypeName) || 0) + r.amount));
    const data = [...byType.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([label, v], i) => ({
        label,
        value: Math.round(v),
        color: TREATMENT_COLORS[i % TREATMENT_COLORS.length],
      }));
    chartList.push({
      key: "treatmentBars",
      type: "bar",
      title: "הכנסות לפי סוג טיפול (נגבה)",
      data,
    });
  }

  if (charts.monthBars && monthsInRange(startDate, endDate) > 1) {
    const byMonth = new Map();
    rows.forEach((r) => {
      const k = monthKeyOf(r.date);
      if (!byMonth.has(k)) byMonth.set(k, { collected: 0, expected: 0 });
      const m = byMonth.get(k);
      if (r.isCollected) {
        m.collected += r.amount;
        m.expected += r.amount;
      } else if (r.bucket === "pending" || r.bucket === "expected") {
        m.expected += r.amount;
      }
    });
    const keys = [...byMonth.keys()].sort();
    chartList.push({
      key: "monthBars",
      type: "groupedBar",
      title: "נגבה מול צפוי לפי חודש",
      series: [
        { key: "collected", label: "נגבה", color: "#4dd9b4" },
        { key: "expected", label: "צפוי", color: "#cbd5e1" },
      ],
      data: keys.map((k) => ({
        label: monthLabelOf(k),
        collected: Math.round(byMonth.get(k).collected),
        expected: Math.round(byMonth.get(k).expected),
      })),
    });
  }

  // ─── Tables ───
  const tables = [];

  if (sections.transactions) {
    tables.push({
      key: "transactions",
      title: "פירוט תנועות",
      columns: [
        { key: "date", label: "תאריך" },
        { key: "patientName", label: "מטופל/ת" },
        { key: "therapistName", label: "מטפל/ת" },
        { key: "treatmentTypeName", label: "סוג טיפול" },
        { key: "method", label: "אמצעי תשלום" },
        { key: "status", label: "סטטוס" },
        { key: "amount", label: "סכום" },
      ],
      rows: rows.map((r) => ({
        date: formatDate(r.date),
        patientName: r.patientName,
        therapistName: r.therapistName,
        treatmentTypeName: r.treatmentTypeName,
        method: r.paymentMethod ? PAYMENT_METHOD_LABELS[r.paymentMethod] || r.paymentMethod : "—",
        status: bucketLabel(r.bucket),
        statusColor: bucketColor(r.bucket),
        amount: formatCurrency(r.amount),
      })),
    });
  }

  if (sections.byPatient) {
    const byPatient = new Map();
    rows.forEach((r) => {
      const k = r.patientId || `name:${r.patientName}`;
      if (!byPatient.has(k)) {
        byPatient.set(k, { name: r.patientName, count: 0, collected: 0, pending: 0, expected: 0 });
      }
      const p = byPatient.get(k);
      p.count += 1;
      if (r.isCollected) p.collected += r.amount;
      else if (r.bucket === "pending") p.pending += r.amount;
      else if (r.bucket === "expected") p.expected += r.amount;
    });
    const list = [...byPatient.values()].sort((a, b) => b.collected - a.collected);
    tables.push({
      key: "byPatient",
      title: "סיכום לפי מטופל",
      columns: [
        { key: "name", label: "מטופל/ת" },
        { key: "count", label: "תנועות" },
        { key: "collected", label: "נגבה" },
        { key: "pending", label: "ממתין" },
        { key: "expected", label: "צפוי" },
      ],
      rows: list.map((p) => ({
        name: p.name,
        count: String(p.count),
        collected: formatCurrency(p.collected),
        pending: p.pending > 0 ? formatCurrency(p.pending) : "—",
        expected: p.expected > 0 ? formatCurrency(p.expected) : "—",
      })),
    });
  }

  return {
    rowCount: rows.length,
    totals,
    kpis: sections.kpis ? kpis : [],
    charts: chartList,
    tables,
    statusLabels: FIN_STATUS_OPTIONS.filter((s) => statuses.has(s.key)).map((s) => s.label),
  };
}
